import type { TextProps } from '@chakra-ui/react';
import { Text } from '@chakra-ui/react';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import type { ReactNode } from 'react';

import { getDecimalsStyles } from './utils/getDecimalsStyles';
import { getPriceText } from './utils/getPriceText';
import { getStrikeThroughStyles } from './utils/getStrikeThroughStyles';

interface FormatPriceProps extends TextProps {
  amount: number;
  isCurrencyCodeHidden?: boolean;
  showFreeIfZero?: boolean;
  hasStrikeThrough?: boolean;
  beforeText?: ReactNode;
  afterText?: ReactNode;
  shouldRoundUp?: boolean;
}

export function FormatPrice({
  amount,
  isCurrencyCodeHidden,
  showFreeIfZero,
  hasStrikeThrough,
  beforeText,
  afterText,
  shouldRoundUp,
  sx: propsSx,
  ...props
}: FormatPriceProps) {
  const { size } = props;
  const {
    value: { countryCode, currencyCode },
    isLoading: isLocaleLoading,
  } = useLocalStorage(UserLocale);

  if (isLocaleLoading) {
    return null;
  }

  const decimalsStyles = getDecimalsStyles(size);
  const strikeThroughStyles = getStrikeThroughStyles(hasStrikeThrough);

  const defaultProps = size
    ? {}
    : {
        fontFamily: 'inherit',
        fontSize: 'inherit',
        letterSpacing: 'inherit',
        lineHeight: 'inherit',
      };

  const sx = {
    ...decimalsStyles,
    ...strikeThroughStyles,
    textWrapMode: 'nowrap',
    ...(propsSx || {}),
  };

  const priceText = getPriceText({
    amount,
    countryCode,
    currencyCode,
    isCurrencyCodeHidden,
    showFreeIfZero,
    shouldRoundUp,
  });

  return (
    <Text as="span" {...defaultProps} sx={sx} {...props}>
      {beforeText}
      {priceText}
      {afterText}
    </Text>
  );
}
